// component-test-check-ignore
import React from 'react';
import { Box, Flex, Icon, Text, Button } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';
import { useExperiments } from 'sellDirect/helpers/ExperimentProvider';
import { useClosePriceAdjustment } from 'sellDirect/graphql/useClosePriceAdjustment';
import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

interface PriceIncreasePromptProps {
  amount: number;
  handleClick: () => void;
}

/** Internal PriceIncreasePrompt without context */
export const PriceIncreasePromptWithoutContext: React.FC<PriceIncreasePromptProps> = ({
  amount,
  handleClick,
}) => {
  const { isMobile } = useDevice();
  return (
    <Flex
      p={7}
      borderRadius="semiRounded"
      border={`1px solid ${colors.neutrals30}`}
      backgroundColor="neutrals0"
      justifyContent="space-between"
    >
      <Flex alignItems="center">
        <Icon
          name="arrow-up"
          color="neutrals0"
          size={32}
          backgroundColor="green50"
          minWidth="40px"
          minHeight="40px"
          borderRadius="roundedSquare"
          alignItems="center"
          justifyContent="center"
        />
        <Box fontSize={['s00', null, 's1']} lineHeight={['s00', null, 's1']} ml="5">
          <Flex flexDirection="column" justifyContent="center">
            <Text>
              <Box as="span" fontWeight={['regular', null, 'semibold']}>
                Based on your responses, your final offer could go up by{' '}
                {centsToCurrencyString(amount)} or more.{' '}
              </Box>
              {!isMobile && <br />}
              Market conditions and your video walkthrough also affect your offer.
            </Text>
          </Flex>
        </Box>
      </Flex>
      <Flex alignItems="center">
        <Button
          analyticsName="cake-increased-price-prompt-close"
          aria-label="Close increased price prompt"
          onClick={handleClick}
          variant="ghost"
          width="40px"
          height="40px"
        >
          <Icon name="close" minHeight="14px" minWidth="14px" _hover={{ bgColor: 'transparent' }} />
        </Button>
      </Flex>
    </Flex>
  );
};

/**
 * Part of the price adjustment experiment. We display this to users who
 * have had their offers increase after finishing the flow. Clicking on the 'close'
 * icon will send a request to the backend and this should only be shown until it has been closed once.
 * We will only be showing this once per lead so we will not show this for those leads that have already been through
 * the new flow before.
 */
export const PriceIncreasePrompt: React.FC = () => {
  const { priceAdjustment } = useExperiments();
  const { updateClosedPriceAdjustment, isResultOpen } = useClosePriceAdjustment(
    priceAdjustment.showIncreaseResult,
  );

  if (!isResultOpen) return null;

  return (
    <PriceIncreasePromptWithoutContext
      amount={priceAdjustment.increaseInCents}
      handleClick={updateClosedPriceAdjustment}
    />
  );
};
